import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "urql";

import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import {
    Table,
    TableBody,
    TableCaption,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { graphql } from "@/gen";
import { ArticlesQuery } from "@/gen/graphql";
import { useLimitOffsetPagination } from "@/utils/pagination";

const articlesQuery = graphql(`
    query Articles($limit: Int!, $offset: Int!) {
        articles(input: { pagination: { limit: $limit, offset: $offset } }) {
            total
            items {
                id
                title
                status
                content {
                    __typename
                }
            }
        }
    }
`);

export default function ArticlesRoute() {
    const [{ limit, offset }] = useLimitOffsetPagination();

    const [result] = useQuery({
        query: articlesQuery,
        variables: {
            limit,
            offset,
        },
    });

    const { data, fetching } = result;

    return (
        <div className="m-4 flex flex-col gap-4">
            <PageHeader
                title="Articles"
                actions={
                    <Button asChild>
                        <Link to="/articles/new">New article</Link>
                    </Button>
                }
            />

            {data && data.articles.items.length > 0 ? (
                <ArticleTable articles={data.articles.items} isFetching={fetching} />
            ) : fetching ? (
                <div>Loading</div>
            ) : (
                <div>No articles</div>
            )}
        </div>
    );
}

type ArticleItem = ArticlesQuery["articles"]["items"][0];
type ArticleTableProps = {
    articles: ArticleItem[];
    isFetching: boolean;
};
function ArticleTable({ articles }: ArticleTableProps) {
    const navigate = useNavigate();

    return (
        <Table>
            <TableCaption>Content</TableCaption>
            <TableHeader>
                <TableHead>Title</TableHead>
                <TableHead>Content type</TableHead>
                <TableHead>Status</TableHead>
            </TableHeader>
            <TableBody>
                {articles.map(article => (
                    <TableRow
                        key={article.id}
                        className="cursor-pointer"
                        onClick={() => navigate(article.id)}
                    >
                        <TableCell>{article.title}</TableCell>
                        <TableCell>{formatContentType(article.content.__typename)}</TableCell>
                        <TableCell>{`${article.status[0]}${article.status.slice(1).toLowerCase()}`}</TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
}

function formatContentType(kind: ArticleItem["content"]["__typename"]) {
    switch (kind) {
        case "RichTextArticle":
            return "Rich text";
        case "ExternalUrlArticle":
            return "External URL";
        case "StoryArticle":
            return "Story article";
        default:
            throw new Error(`Unknown content type: ${kind}`);
    }
}
